/**
 * ES6 Module
 * File Upload Controller module
 * A central module that will allow storing sharable values in one place will provide access to the sharable methods
 * that can be used with cs-file-upload component across the app
 *
 * ©2020 CoreStream Ltd
 *
 * @format
 */

import $mimeType from '@/js/modules/csMimeType.js';

// Private properties
// NOTE: when we add new file formats we must go and download a placeholder SVG from font awesome if the BE cannot generate preview images
// we are currently using the light version of the icons so start here: https://fontawesome.com/v5.15/icons?d=gallery&p=2&s=light

const $_defaultMaxFileSize = 5 * 1000 * 1000; // 5MB

// NOTE: this list is hardcode so we need to change it whenever a new thing is previewable on the BE
const $_previewableFileTypesAndExtensions = [
	//uncomment me for thumbnails
	//'image/png',
	//'image/jpg',
	//'image/jpeg',
	//'image/gif'
];

let _isParsed = false;
let _fileMaxAllowedSize = $_defaultMaxFileSize;
let _allowedMimeTypes = [];

// Private methods
/**
 * @private
 * @description
 * Parses provided array of file extension to a format that is expected by FilePond component
 * Converts all formats to a commong mime-type format and store results in controller $_fileFromats array
 * @param { Array } fileExtensions
 * @returns void
 */
const _parseFileExtensions = (fileExtensions) => {
	fileExtensions = fileExtensions.filter((v) => typeof v === 'string');
	fileExtensions = Array.from(new Set(fileExtensions));

	_allowedMimeTypes = $mimeType.getAllMimeTypesFromExtensions(fileExtensions);
};

const $_browserIsIE11 = !window.ActiveXObject && 'ActiveXObject' in window;

//PUBLIC INTERFACE
const $csFileUpload = {};
export default $csFileUpload;

//PUBLIC IMPLEMENTATION METHODS

/**
 * @public
 * @description
 * Controls parsing a provided file extensions array in the controller module
 *
 * @param { Array } fileExtensions the array of file extensions
 * @param { Boolean } reset the boolean indicator to trigger re-parsing
 *
 * @returns void
 */
$csFileUpload.initialFileExtensions = (fileExtensions, reset) => {
	if (reset != undefined && reset === true) _isParsed = false;
	if (_isParsed == true) return;

	_parseFileExtensions(fileExtensions);

	_isParsed = true;
};

/**
 * @public
 * @description
 * Sets the value of internal $_fileMaxAllowedSize
 *
 * @param { Number } size
 */
$csFileUpload.setFileMaxAllowedSize = (size) => {
	if (!Number.isFinite(size)) return;

	_fileMaxAllowedSize = size;
};

/**
 * @public
 * @description
 * Returns available image formats
 *
 * @return { Array }
 */
$csFileUpload.getImageFormats = () => {
	return _allowedMimeTypes.filter((f) => f.includes('image'));
};

/**
 * @public
 * @description
 * Returns available video formats
 *
 * @return { Array }
 */
$csFileUpload.getVideoFormats = () => {
	return _allowedMimeTypes.filter((f) => f.includes('video'));
};

/**
 * @public
 * @description
 * Returns available file formats in a friendly format for FilePond component
 *
 * @return { Array }
 */
$csFileUpload.getFileFormats = () => {
	return _allowedMimeTypes;
};

/**
 * @public
 * @description
 * Returns maximum allowed file size
 *
 * @return { Number }
 */
$csFileUpload.getFileMaxAllowedSize = () => {
	return _fileMaxAllowedSize;
};

/**
 * @public
 * @description
 * Returns the maximum allowed file size in human readable format
 *
 * @return { String }
 */
$csFileUpload.getFileMaxAllowedSizeHumanReadable = () => {
	return `${_fileMaxAllowedSize / 1000 / 1000}MB`;
};

/**
 * @public
 * @description
 * Check the file type (mime type) string to see if we are able to preview this type.
 * Note: I decided not to split this out into two methods (one for mime and one for extension) to save complexity
 *
 * @param {string} fileTypeOrExtension The mime type string, e.g. "image/png", or file extension, e.g. "png"
 */
$csFileUpload.isFileExtensionOrFileTypePreviewable = (fileTypeOrExtension) => {
	// if we have no file extension we can't inspect it
	if (fileTypeOrExtension == undefined) {
		return false;
	}

	// IE11 is not capable of handling the urls we use for thumbnails so we cannot display a preview
	if ($_browserIsIE11) {
		return false;
	}
	return $_previewableFileTypesAndExtensions.indexOf(fileTypeOrExtension) !== -1;
};

/**
 * @public
 * @description
 * Generates the file object for file pond
 * @param {string} fileName The file name
 * @param {string} fileRef The file reference from the BE
 * @param {string} thumbnailRequestURL Where to go to get the thumbnail, should include the token if it is an API url
 */
$csFileUpload.generateFileObjectForFilePond = (fileName = null, fileRef = null, fileId = null, thumbnailRequestURL) => {
	let extension = fileName.substring(fileName.lastIndexOf('.'));
	let mimeType = $mimeType.getMimeTypeFromExtension(extension);

	return {
		source: {
			fileName,
			fileRef
		},
		options: {
			type: 'local',
			file: {
				// if we don't give filepond the filename then it will download the entire attachment to get it :)
				name: fileName,
				size: 100,
				type: mimeType
			},
			metadata: {
				originalData: {
					fileRef,
					fileId,
					fileName
				},
				// combine the thumbnail request url (sometimes API, sometimes local) with the token
				// sometimes this token is empty (for local placeholders)
				poster: thumbnailRequestURL,
				thumbnailError: false
			}
		}
	};
};

/**
 * @public
 * @description
 * gets the group type from the extension
 */
$csFileUpload.getFileIconNameFromExtension = (extension) => {
	if (extension == undefined || extension === '') return '';
	//check specific extesnion mappings
	switch (extension) {
		case 'doc':
			return 'word.svg';
		case 'docx':
			return 'word.svg';
		case 'pdf':
			return 'pdf.svg';
		case 'ppt':
			return 'powerpoint.svg';
		case 'pptx':
			return 'powerpoint.svg';
		case 'xls':
			return 'excel.svg';
		case 'xlsx':
			return 'excel.svg';
	}
	//if we do not have a specific fileIcon, use the groupType icon - defaults to file.svg
	let mimeType = $mimeType.getMimeTypeFromExtension(`.${extension}`);
	let groupType = mimeType.replace(/\/.*/, '');
	switch (groupType) {
		case 'image':
			return 'image.svg';
		case 'video':
			return 'video.svg';
		default:
			return 'file.svg';
	}
};

//INITIALIZATION
//TODO should we do this on the form class not at cs-app?

/**
 *
 * @public
 * @description
 * Fetching platform settings to the module.
 * @param { Object } settings
 */
$csFileUpload.setNetworkCallApplicationSettings = (settings) => {
	if (settings.allowedFileExtensions != undefined) $csFileUpload.initialFileExtensions(settings.allowedFileExtensions, true);
	if (settings.maxAllowedPayload != undefined) $csFileUpload.setFileMaxAllowedSize(settings.maxAllowedPayload);
};
